import React from 'react';
import { motion } from 'motion/react';
import CountdownTimer from '../components/ComingSoon/CountdownTimer';
import WaitlistForm from '../components/ComingSoon/WaitlistForm';
import FeatureCard from '../components/ComingSoon/FeatureCard';
import FloatingElements from '../components/ComingSoon/FloatingElements';
import Footer from '../components/ComingSoon/Footer';
import CustomCursor from '../components/CustomCursor';
import { heroData, featuresData } from '../data/mock';

const ComingSoonPage = () => {
  return (
    <div className="min-h-screen bg-[#F8F7FA] relative overflow-hidden">
      {/* Custom Cursor */}
      <CustomCursor />
      
      {/* Floating gradient elements */}
      <FloatingElements />
      
      {/* Main content */}
      <div className="relative z-10">
        {/* Hero Section */}
        <section className="min-h-screen flex flex-col items-center justify-center px-4 md:px-6 py-12">
          <div className="max-w-4xl mx-auto text-center">
            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                type: 'spring',
                damping: 80,
                stiffness: 400,
                delay: 0.1
              }}
              className="mb-8"
            >
              <motion.img
                src={heroData.logoUrl}
                alt="POSTAÍ Logo"
                className="w-32 h-32 md:w-40 md:h-40 mx-auto object-contain drop-shadow-2xl"
                whileHover={{ scale: 1.05, rotate: 5 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              />
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                type: 'spring',
                damping: 80,
                stiffness: 400,
                delay: 0.2
              }}
              className="text-5xl md:text-7xl lg:text-8xl font-semibold text-[#0A0A0A] mb-4 tracking-tight"
              style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}
            >
              {heroData.title}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                type: 'spring',
                damping: 80,
                stiffness: 400,
                delay: 0.25
              }}
              className="text-xl md:text-2xl text-[#008055] font-medium mb-6"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {heroData.subtitle}
            </motion.p>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                type: 'spring',
                damping: 80,
                stiffness: 400,
                delay: 0.3
              }}
              className="text-lg md:text-xl text-[#3C3B3D] max-w-2xl mx-auto mb-12 leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              {heroData.description}
            </motion.p>

            {/* Launch label */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                type: 'spring',
                damping: 80,
                stiffness: 400,
                delay: 0.35
              }}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#D98A8C]/20 via-[#E1C567]/20 to-[#DFB573]/20 border border-white/50 shadow-sm mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-[#008055] animate-pulse" />
              <span
                className="text-sm font-medium text-[#3C3B3D]"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Lançamento em 15 de Janeiro de 2026
              </span>
            </motion.div>

            {/* Countdown */}
            <div className="mb-16">
              <CountdownTimer targetDate={heroData.launchDate} />
            </div>

            {/* Waitlist Form */}
            <WaitlistForm />
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-100px' }}
              transition={{
                type: 'spring',
                damping: 80,
                stiffness: 400
              }}
              className="text-center mb-16"
            >
              <h2
                className="text-3xl md:text-4xl font-semibold text-[#0A0A0A] mb-4"
                style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.01em' }}
              >
                O que está por vir
              </h2>
              <p
                className="text-lg text-[#3C3B3D] max-w-xl mx-auto"
                style={{ fontFamily: 'Inter, sans-serif' }}
              >
                Recursos poderosos para transformar sua presença no Instagram
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featuresData.map((feature, index) => (
                <FeatureCard key={feature.id} feature={feature} index={index} />
              ))}
            </div>
          </div>
        </section>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default ComingSoonPage;
